import { SupportPayload, AssistenzaPratichePayload } from './../../js/product/product.models';
import { envConfig } from './../../js/config/envconfig';
import { isValidTemplate } from "@wordpress/block-editor/store/selectors";
import {
  SurveyStep,
  SurveyState,
  ActionListeners,
  ListenerRecord,
  SurveyBreadCrumb,
  SurveyAnswer,
} from "./survey-models";
import { HASH_SEPARATOR } from "./survey.constants";
import Utils from "../../js/utils";
import productApi from "../../js/product/product-api";
import Validators from '../../js/validators';
import { LoginLocalStorageData } from '../../js/header.models';

/**
 * @description classe di metodi di utility statici
 */
export class SurveyUtils {
  static async doSupportCall(cap: string, email: string) {
    const infoUtente = (<LoginLocalStorageData>JSON.parse(localStorage.getItem("info_utente_reducer")))?.info_utente;
    const postPayload: AssistenzaPratichePayload = {
      idTriplettaInterazione: php_pars.idtripletta.nocoverage,
      domandaAssistenza: `Mancata copertura per cap: ${cap}`,
      mezzoRicontatto: "email"
    };
    if (infoUtente?.recupera_info_utente?.result) {
      postPayload.datiAnagrafica = {
        nome: infoUtente.recupera_info_utente.result.nome ?? "anonimo",
        cognome: infoUtente.recupera_info_utente.result.cognome ?? "anonimo" ,
        email: email,
        numeroTelefono: infoUtente.recupera_info_utente.result.numeroTelefono
      }
    } else {
      postPayload.datiAnagrafica = { email: email, nome:"anonimo",cognome:"anonimo" };
    }
    return productApi.postSupportData(postPayload);
  }
  //cache per non dover rilanciare sempre la chain di operatori

  /**
   * @description Ottengo un array con tutti gli steps degerarchizzati (cacheati dopo il printo lancio)
   * @param  {SurveyStep} root step principale
   * @returns {SurveyStep[]} array
   */
  private static FlattenAllSteps(root: SurveyStep): SurveyStep[] {
    if (root.children) {
      return [
        root,
        ...root.children.map((res) => this.FlattenAllSteps(res)).flat(1),
      ];
    } else {
      return [root];
    }
  }

  /**
   * @description ottengo tutte le risposte (stringhe) dato uno step
   * @param  {SurveyStep} step
   * @returns {Promise<string[]>}
   */
  public static async FetchAnswers(step: SurveyStep): Promise<string[]> {
    return step?.children.map((x) => x.name) ?? [];
  }
  /**
   * @param  {Array<SurveyBreadCrumb>} path path da cui ottenere lo step
   * @param  {SurveyStep} root
   * @returns {Promise<SurveyStep>} step dato dal path
   */
  public static async GetQuestionByPath(
    path: Array<SurveyBreadCrumb>,
    root: SurveyStep
  ): Promise<SurveyStep> {
    let currentStep: SurveyStep = root;
    for (let crumb of path) {
      currentStep = currentStep.children.find((res)=> res.name?.trim() == crumb.name?.trim());
    };
    return currentStep;
  }

  /**
   * @description ottiene splitta e decodifica l'hash attuale
   * @return {Promise<string[]>} array con le risposte date
   */
  public static async GetUrlPath(): Promise<string[]> {
    let pathString = location.hash.substring(1);
    return pathString.split(HASH_SEPARATOR).map(decodeURIComponent);
  }
  /**
   * @description ottiene lo stato attuale del sondaggio in base all'url
   * @param  {SurveyStep} root step principale
   * @returns {Promise<SurveyState>}  nuovo oggetto stato valorizzato in base all'url corrente
   */
  public static async getStepHistory(
    root: SurveyStep,
    stateCache: SurveyState = { paths: [] }
  ): Promise<{ state: SurveyState; cache: SurveyState }> {
    const state: SurveyState = { paths: [] };
    if (location.hash) {
      const paths = await this.GetUrlPath();

      for (let i = 0; i < paths.length; i++) {
        const bc: SurveyBreadCrumb = {
          question: (
            i == 0 ? root : await this.GetQuestionByPath(state.paths, root)
          ).question,
          name: paths[i],
          lastAnswer: stateCache.paths[i + 1]
            ? stateCache.paths[i + 1].name
            : null,
        };
        state.paths.push(bc);
      }
    }
    //se siamo all'inizio del questionario, imposto lo step corrente alla radice
    if (state.paths.length == 0) {
      state.currentStep = root;
    } else {
      //sennò prendo l'ultimo
      state.currentStep = state.paths.at(state.paths.length - 1);
    }
    if (stateCache.paths.length <= state.paths.length) {
      stateCache = JSON.parse(JSON.stringify(state)); //salvo una versione di cache per gestire eventualmente il forward
    } else {
      state.rootAnswer = stateCache.paths[0].name;
      state.forward = true;
    }
    state.insertedCap = await this.getZipCodeQueryString();
    return { state: state, cache: stateCache };
  }
  /**
   * @description rimuove i caratteri speciali e li sostituisce on - per essere compatibili (e comunque leggibili) con l'html
   * @param  {string} answer domanda da sanizzare
   * @return {string} domanda a cui vengono sostituiti i caratteri scomodi per essere compatibili con l'html (sostituiti con -)
   */
  public static sanitizeText(answer: string): string {
    const regex = /[ $&+,:;=?@#|'<>.^*()%!-/]/g;
    return answer.replace(regex, "-").toLowerCase();
  }
  public static async checkZipcode(
    zipCode: string,
    idMestiere: string
  ): Promise<{ success: boolean; errorMessage?: string }> {
    if (!Validators.validateCap(zipCode)) {
      return { success: false, errorMessage: "Il CAP inserito non è valido." };
    }
    //controllo la copertura del cap
    let response = (await productApi.checkProductCoverage(zipCode, null, idMestiere));
    return { //senza errorMessage verrà considerato true coperto, false non coperto
      success: response?.coperto ?? false
    }
  }
  /**
   * @param  {HTMLElement} element elemento a cui attaccare il listener
   * @param  {ListenerRecord} record elemento della map contenente la funzione handler e il tipo di listener
   * @param  {ActionListeners} listeners mappa dei listener da cui rimuovere il listener eliminato
   */
  public static async registerHandler(
    element: HTMLElement,
    record: ListenerRecord,
    listeners: ActionListeners
  ) {
    element.addEventListener(
      Array.isArray(record.eventType)
        ? record.eventType.join(" ")
        : <string>record.eventType,
      record.handler
    );
    listeners.set(element, record);
  }
  /**
   * @description deregistra un listener
   * @param  {HTMLElement} element elemento a cui rimuovere il listener
   * @param  {ListenerRecord} record elemento della map contenente la funzione handler e il tipo di listener
   * @param  {ActionListeners} listeners mappa dei listener da cui rimuovere il listener eliminato
   */
  public static async deregisterHandler(
    element: HTMLElement,
    record: ListenerRecord,
    listeners: ActionListeners
  ) {
    element.removeEventListener(
      Array.isArray(record.eventType)
        ? record.eventType.join(" ")
        : <string>record.eventType,
      record.handler
    );
    listeners.delete(element);
  }
  public static async addZipCodeQueryString(cap: string): Promise<void> {
    //aggiungo il cap all'url, per gestire refresh etc
    if ("URLSearchParams" in window && cap) {
      var searchParams = new URLSearchParams(window.location.search);
      searchParams.set("cap", cap);
      var newRelativePathQuery =
        window.location.pathname +
        "?" +
        searchParams.toString() +
        window.location.hash;
      history.replaceState(null, "", newRelativePathQuery); //rimpiazzo la history, in quanto il rerendering lo gestisco altrove
    }
  }
  public static async getZipCodeQueryString(): Promise<string> {
    //aggiungo il cap all'url, per gestire refresh etc
    if ("URLSearchParams" in window) {
      var searchParams = new URLSearchParams(window.location.search);
      return searchParams.get("cap")?.trim();
    }
  }
  public static CalcProgressBar(
    state: SurveyState,
    root: SurveyStep,
    selectedStep: SurveyStep
  ): number {
    const completedSteps = state.paths.length; //steps completati
    const longestRouteFromSelectedStep = this.GetLongestPath(selectedStep);
    const totalSteps = completedSteps + longestRouteFromSelectedStep;
    return 25 + Math.round((75 * completedSteps) / totalSteps);
  }
  private static GetLongestPath(root: SurveyStep): number {
    let depth = 0;

    if (Array.isArray(root.children) && root.children.length) {
      depth += 1;

      let subDepth = 0;
      for (const child of root.children) {
        subDepth = Math.max(subDepth, this.GetLongestPath(child));
      }

      depth += subDepth;
    }

    return depth;
  }

  public static async flattenSurveyData(items: SurveyBreadCrumb[]): Promise<SurveyAnswer[]> {
    let flattenedAnswers: SurveyAnswer[] = [];
    items.forEach((item) => {
      flattenedAnswers.push({
        domanda: item.question,
        risposta: item.name,
      });
    });

    return flattenedAnswers;
  }

  public static async doSupportRedirect(answers: SurveyAnswer[], payload?: SupportPayload, idTripletta?: number) {
    console.log(
      `%cRedirect a supporto...`,
      "color: darkslategrey; background-color: darkorange; padding: 2px; border-radius: 3px;"
    );
    let insertedCap = await this.getZipCodeQueryString();
      for(let element of answers){
        for(let item in element){
          element[item as keyof SurveyAnswer] =
            Utils.htmlDecode(element[item as keyof SurveyAnswer] || "") || "";
        }
      }
    let dataPayload: SupportPayload = {
      cap: insertedCap,
      flussoDomande: answers,
      email: payload?.email,
      idMestiere: payload?.idMestiere || undefined,
      privacy: payload?.privacy,
      cmp: sessionStorage.getItem("cmp") || undefined
    };

    let encodedAnswers = Utils.encodeB64(JSON.stringify(dataPayload));
    let redirectURL: URL = new URL(`${envConfig.SUPPORTPATH}assistenza/contatti`);
    let searchParams = new URLSearchParams(redirectURL.search);
    searchParams.append("pinfo", encodedAnswers);
    if (idTripletta) {
      searchParams.append("idTripletta", idTripletta.toString());
    }
    searchParams.toString();

    window.location.href = redirectURL + "?" + searchParams.toString();
  }
  /**
   * @description renderizza l'html passato nell'elemento oggetto NB: questo DEVE essere l'unico metodo a modificare effettivamente la dom
   * @param  {HTMLElement} element elemento su cui renderizzare l'html passato
   * @param  {string} html da renderizzare
   * @returns {Promise<void>}
   */
  public static async render(
    element: HTMLElement,
    html: string
  ): Promise<void> {
    if (!element) {
      console.error(
        `%cError loading element element`,
        "color: red; background-color: darkseagreen; padding: 2px; border-radius: 3px;'"
      );
      return;
    }
    element.innerHTML = html;
  }

}
